import type { ReactNode } from "react";

// Static legal copy for L1-U3 (ADR 0028 D4/D5). This is a committed DRAFT meant
// to be refined by the operator before public launch; the structure, routing,
// and acceptance gate are production. The privacy policy intentionally discloses
// the telemetry the app uses (Sentry + product analytics) and the data-retention
// posture, because ADR 0028 D5 requires the policy to cover what it powers.
//
// Document VERSIONS are owned by the backend registry
// (workspaces/application/identity/legal_documents.py) and surfaced through the
// acceptance status API; this module owns only the human-readable copy.

export type LegalDocumentKey = "terms" | "privacy" | "disclaimer";

export interface LegalDocumentContent {
  readonly key: LegalDocumentKey;
  readonly title: string;
  readonly path: string;
  /** One-line description shown on the acceptance gate. */
  readonly summary: string;
  readonly body: ReactNode;
}

function Section({ heading, children }: { heading: string; children: ReactNode }) {
  return (
    <section className="space-y-2">
      <h2 className="font-display text-lg font-medium text-on-surface">{heading}</h2>
      {children}
    </section>
  );
}

export const LEGAL_DOCUMENT_ORDER: readonly LegalDocumentKey[] = ["terms", "privacy", "disclaimer"];

export const LEGAL_DOCUMENTS: Record<LegalDocumentKey, LegalDocumentContent> = {
  terms: {
    key: "terms",
    title: "Terms of Service",
    path: "/terms",
    summary: "The rules for using the app.",
    body: (
      <>
        <p>
          These Terms govern your use of this trading journal (the &ldquo;Service&rdquo;). By
          creating an account or using the Service you agree to them. If you do not agree, do not
          use the Service.
        </p>
        <Section heading="Your account">
          <p>
            You are responsible for the activity under your account and for keeping your sign-in
            credentials secure. The Service is provided for your own record-keeping; you may not
            resell or redistribute access.
          </p>
        </Section>
        <Section heading="Your data">
          <p>
            You retain ownership of the trade records and notes you enter. You grant us only the
            limited rights needed to store and display that data back to you and to operate the
            Service. We do not sell your data.
          </p>
        </Section>
        <Section heading="Acceptable use">
          <p>
            Do not attempt to access other tenants&rsquo; data, disrupt the Service, or upload
            unlawful content. We may suspend accounts that violate these Terms.
          </p>
        </Section>
        <Section heading="Availability and changes">
          <p>
            The Service is offered on an &ldquo;as is&rdquo; basis during this beta and may change
            or be unavailable at times. We may update these Terms; when we do, we will ask you to
            accept the new version before continuing to use the Service.
          </p>
        </Section>
      </>
    ),
  },
  privacy: {
    key: "privacy",
    title: "Privacy Policy",
    path: "/privacy",
    summary: "What we collect, why, and how long we keep it.",
    body: (
      <>
        <p>
          This policy explains what personal data the Service processes and why. It covers the
          financial records you enter and the operational telemetry the Service relies on.
        </p>
        <Section heading="What we collect">
          <p>
            <strong>Account data</strong> (your email and authentication identifier, via our
            identity provider Auth0) and the <strong>trading data</strong> you enter or import
            (trades, accounts, instruments, notes). This data is isolated per tenant.
          </p>
        </Section>
        <Section heading="Telemetry we use">
          <p>
            <strong>Error tracking (Sentry).</strong> We send diagnostic error reports to Sentry
            to detect and fix failures. These may include technical context and your tenant
            identifier, but not your trade contents beyond what is needed to diagnose an error.
          </p>
          <p>
            <strong>Product analytics.</strong> We record high-level usage events (such as sign-in,
            import, and viewing an insight) to understand activation and improve the Service. We do
            not use this to build advertising profiles.
          </p>
        </Section>
        <Section heading="Data retention">
          <p>
            Account and trading data are retained until you delete your account. Import history is
            retained for one month. Telemetry is retained for a limited window for debugging and
            product analysis. After deletion, residual copies may persist briefly in encrypted
            backups before they age out.
          </p>
        </Section>
        <Section heading="Your choices">
          <p>
            You can export your data and request deletion of your account from within the app. For
            questions about this policy, contact the team.
          </p>
        </Section>
      </>
    ),
  },
  disclaimer: {
    key: "disclaimer",
    title: "Financial Disclaimer",
    path: "/disclaimer",
    summary: "This is a journaling tool, not financial advice.",
    body: (
      <>
        <p>
          This Service is a personal record-keeping and analytics tool for your own trading
          activity. It is <strong>not</strong> investment, financial, tax, or legal advice, and
          nothing in it is a recommendation to buy or sell any security.
        </p>
        <Section heading="No reliance">
          <p>
            Analytics, charts, and figures are derived from the data you enter and may contain
            errors or omissions. Do not rely on them for trading decisions. Trading involves
            substantial risk of loss; past performance does not indicate future results.
          </p>
        </Section>
        <Section heading="Your responsibility">
          <p>
            You are solely responsible for your trading decisions and for verifying the accuracy of
            the data you import or enter. Consult a licensed professional before making financial
            decisions.
          </p>
        </Section>
      </>
    ),
  },
};
